import { useEffect, useMemo, useState } from 'react'
import { useT } from '@transifex/react'
import { ApiOutlet } from '@vend/types'
import { useRegisterManager } from '../../../../../../ng-compat/hooks'
import { outletsStore } from '../../../../../../factories/database/outlets-store'
import { useAppSelector } from '../../../../../../state/hooks'
import { selectSaleLocalId } from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { getAvailabilityByOutletId } from '../../../../../../util/cross-location-fulfilment'
import {
  OutletDropdownListOption,
  OutletOption,
  OutletSelectProps,
} from '../OutletSelectTypes'

const OUTLET_SEARCH_MIN_CHARS = 2

const getOutletAddress = (outlet: ApiOutlet): string =>
  [
    outlet.physical_address_1,
    outlet.physical_address_2,
    outlet.physical_suburb,
    outlet.physical_city,
    outlet.physical_postcode,
    outlet.physical_state,
  ]
    .filter(Boolean)
    .join(', ')

const toOutletOption = (
  outlet: ApiOutlet,
  availabilityByOutletId?: Map<string, number>
): OutletOption => {
  const option: OutletOption = {
    id: outlet.id,
    name: outlet.name,
    address: getOutletAddress(outlet),
  }

  if (availabilityByOutletId) {
    option.available = availabilityByOutletId.get(outlet.id) ?? 0
  }

  return option
}

const sortOutlets = (
  outlets: readonly OutletOption[],
  activeOutletId?: string
) => {
  const activeOutlet = outlets.find(outlet => outlet.id === activeOutletId)

  if (!activeOutlet) {
    return [...outlets].sort((a, b) => a.name.localeCompare(b.name))
  }

  const otherOutlets = outlets
    .filter(outlet => outlet.id !== activeOutletId)
    .sort((a, b) => a.name.localeCompare(b.name))

  return [activeOutlet, ...otherOutlets]
}

type LoadOutletOptionsParams = {
  registerManager: ReturnType<typeof useRegisterManager>
  activeOutletId?: string
  productId?: string
  saleLocalId?: string
  includeAvailability: boolean
  isStale?: () => boolean
}

const loadOutletOptions = async ({
  registerManager,
  activeOutletId,
  productId,
  saleLocalId,
  includeAvailability,
  isStale = () => false,
}: LoadOutletOptionsParams): Promise<OutletOption[]> => {
  const allOutlets = await outletsStore.all()
  const eligibleOutlets = registerManager.getEligibleCLFTargetOutlets(
    allOutlets
  )
  const outletIds = eligibleOutlets.map(outlet => outlet.id)

  const availabilityByOutletId =
    includeAvailability && productId && outletIds.length > 0 && !isStale()
      ? await getAvailabilityByOutletId(productId, outletIds, saleLocalId)
      : undefined

  return sortOutlets(
    eligibleOutlets.map(outlet =>
      toOutletOption(outlet, availabilityByOutletId)
    ),
    activeOutletId
  )
}

const resolveSelectedOutletId = (
  options: readonly OutletOption[],
  currentOutletId: string,
  activeOutletId?: string
): string =>
  options.some(outlet => outlet.id === currentOutletId)
    ? currentOutletId
    : options.find(outlet => outlet.id === activeOutletId)?.id ??
      options[0]?.id ??
      ''

export const useOutletSelect = ({
  initialSelectedOutletId,
  productId,
  includeAvailability = false,
  disabled = false,
  onOutletChange,
  onSelectedOutletIdChange,
}: OutletSelectProps = {}) => {
  const t = useT()
  const registerManager = useRegisterManager()
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const [outletOptions, setOutletOptions] = useState<OutletOption[]>([])
  const [isOutletOptionsLoading, setIsOutletOptionsLoading] = useState(true)
  const [selectedOutletId, setSelectedOutletId] = useState(
    initialSelectedOutletId || ''
  )
  const [searchTerm, setSearchTerm] = useState('')
  const activeOutletId = registerManager.getActiveOutlet()?.id

  useEffect(() => {
    let isStale = false

    setIsOutletOptionsLoading(true)

    loadOutletOptions({
      registerManager,
      activeOutletId,
      productId,
      saleLocalId,
      includeAvailability,
      isStale: () => isStale,
    })
      .catch((): OutletOption[] => [])
      .then(options => {
        if (isStale) {
          return
        }

        setOutletOptions(options)
        setSelectedOutletId(currentOutletId =>
          resolveSelectedOutletId(options, currentOutletId, activeOutletId)
        )
        setIsOutletOptionsLoading(false)
      })

    return () => {
      isStale = true
    }
  }, [
    activeOutletId,
    includeAvailability,
    productId,
    registerManager,
    saleLocalId,
  ])

  useEffect(() => {
    if (
      initialSelectedOutletId &&
      outletOptions.some(outlet => outlet.id === initialSelectedOutletId)
    ) {
      setSelectedOutletId(initialSelectedOutletId)
    }
  }, [initialSelectedOutletId, outletOptions])

  const emptyLabel = t('No outlets available', {
    _context: 'Message displayed when there are no outlets available.',
  })
  const noResultsLabel = t('No outlets found', {
    _context: 'Message displayed when no outlets match the search term.',
  })
  const loadingLabel = t('Loading outlets', {
    _context: 'Message displayed when outlets are loading.',
  })

  const selectedOutlet =
    outletOptions.find(outlet => outlet.id === selectedOutletId) ??
    outletOptions.find(outlet => outlet.id === activeOutletId) ??
    outletOptions[0]
  const isEmpty = outletOptions.length === 0
  const isDisabled = disabled || isOutletOptionsLoading || isEmpty
  const triggerOutlet = selectedOutlet ?? {
    id: '',
    name: isOutletOptionsLoading ? loadingLabel : emptyLabel,
  }

  const filteredOutlets = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()

    if (normalizedSearch.length < OUTLET_SEARCH_MIN_CHARS) {
      return outletOptions
    }

    const isNameMatch = (outlet: OutletOption) =>
      outlet.name.toLowerCase().includes(normalizedSearch)
    const isAddressMatch = (outlet: OutletOption) =>
      (outlet.address ?? '').toLowerCase().includes(normalizedSearch)

    // Name matches rank above address-only matches.
    return [
      ...outletOptions.filter(isNameMatch),
      ...outletOptions.filter(
        outlet => !isNameMatch(outlet) && isAddressMatch(outlet)
      ),
    ]
  }, [outletOptions, searchTerm])

  const outletById = useMemo(
    () => new Map(outletOptions.map(outlet => [outlet.id, outlet])),
    [outletOptions]
  )

  const items = useMemo<Array<{ items: OutletDropdownListOption[] }>>(
    () => [
      {
        items: filteredOutlets.map(outlet => ({
          label: outlet.name,
          value: outlet.id,
        })),
      },
    ],
    [filteredOutlets]
  )

  const handleOutletSelect = (item: OutletDropdownListOption) => {
    if (isDisabled) {
      return
    }

    const outlet = outletById.get(item.value)

    if (outlet) {
      setSelectedOutletId(outlet.id)
      setSearchTerm('')
      onOutletChange?.(outlet)
    }
  }

  const shouldShowOutletHint =
    !!activeOutletId && !!selectedOutlet && selectedOutlet.id !== activeOutletId

  useEffect(() => {
    if (isOutletOptionsLoading) {
      return
    }

    onSelectedOutletIdChange?.(selectedOutlet?.id)
  }, [isOutletOptionsLoading, onSelectedOutletIdChange, selectedOutlet?.id])

  const shouldRender = isOutletOptionsLoading || outletOptions.length > 0

  return {
    activeOutletId,
    isOutletOptionsLoading,
    outletOptions,
    selectedOutletId,
    setSelectedOutletId,
    filteredOutlets,
    handleOutletSelect,
    isDisabled,
    isEmpty,
    items,
    outletById,
    searchTerm,
    setSearchTerm,
    selectedOutlet,
    shouldShowOutletHint,
    triggerOutlet,
    emptyLabel,
    noResultsLabel,
    shouldRender,
  }
}
